import { useEffect, useState, useCallback } from "react";
import {
  Save, Trash2, Loader2, Brain, Copy, CheckCheck, Sparkles,
  ChevronDown, ChevronRight, Eye, EyeOff, Edit3, Layers,
  CheckCircle2, Circle, Wand2, Info, ArrowRight,
} from "lucide-react";
import { toast } from "react-toastify";
import {
  getGeneralInstructionBySource,
  createGeneralInstruction,
  updateGeneralInstruction,
  deleteGeneralInstruction,
} from "../../../../apiWithKnowledge";
import { useIngestionJobs } from "../../ingestion/IngestionJobsContext";
import { IngestionQueueStrip, IngestionStatusBadge } from "../../ingestion/IngestionFeedback";

/* ─────────────────────────────────────────────────────────
   Section Definitions — 4 core (required) + 4 extended
───────────────────────────────────────────────────────── */
const SECTIONS = [
  {
    id: "overview",
    icon: "📊",
    title: "Database Overview",
    required: true,
    group: "core",
    question: "What does this database represent?",
    hint: "Describe what this database is about and what business problem it solves.",
    example:
      "This database tracks customer loans, repayments, and risk for an NBFC. It helps analyze delinquency, collections, and overall loan health across home, vehicle, and personal loan products.",
    placeholder:
      "e.g. This is a retail sales database tracking orders, customers, and inventory for a D2C brand operating across 3 regions…",
  },
  {
    id: "tables",
    icon: "📁",
    title: "Tables & Their Purpose",
    required: true,
    group: "core",
    question: "What do the tables in this data represent?",
    hint: "List each table and explain what it stores — the AI will use this to write correct queries.",
    example:
      "- customers: Demographics and contact info (age, income, city)\n- loans: Loan details (amount, tenure, product type, EMI)\n- repayments: Payment history per loan\n- risk_scores: AI-generated recovery probability per customer",
    placeholder:
      "- table_name: what it stores\n- another_table: what it contains and its purpose\n- third_table: …",
  },
  {
    id: "purpose",
    icon: "🎯",
    title: "Why This Data Exists",
    required: true,
    group: "core",
    question: "What decisions or reports does this data drive?",
    hint: "Explain the goals, key use cases, or business questions this data is meant to answer.",
    example:
      "We use this to monitor loan performance, flag at-risk customers early, and optimize collection strategies. Key reports include monthly portfolio health, DPD bucket analysis, and state-wise recovery rates.",
    placeholder:
      "e.g. This data drives weekly sales reviews, helps the growth team identify upsell opportunities, and feeds our churn prediction model…",
  },
  {
    id: "business",
    icon: "🏢",
    title: "Business Context",
    required: false,
    group: "core",
    question: "What business or industry does this represent? (optional but recommended)",
    hint: "Helps the AI use correct terminology, understand domain-specific terms, and give relevant insights.",
    example:
      "Fintech NBFC operating in India. We offer home, personal, and vehicle loans — primarily to salaried customers in Tier 2 and Tier 3 cities. Portfolio size: ₹500Cr+.",
    placeholder:
      "e.g. B2B SaaS company serving mid-market healthcare providers in the US. Revenue model is subscription-based with per-seat pricing…",
  },
  {
    id: "metrics",
    icon: "📈",
    title: "Key Metrics & Important Columns",
    required: false,
    group: "extended",
    question: "Define your most important columns and KPIs",
    hint: "List critical fields and their exact business meaning so the AI interprets them correctly.",
    example:
      "- outstanding_amount: remaining unpaid loan balance\n- collected_amount: total amount recovered so far\n- dpd: days past due (0 = current, >0 = delinquent)\n- recovery_probability: AI-generated score from 0–1",
    placeholder:
      "- column_name: what it means in business terms\n- kpi_name: how it is calculated or derived…",
  },
  {
    id: "rules",
    icon: "⚠️",
    title: "Business Rules & Logic",
    required: false,
    group: "extended",
    question: "Any logic or conditions the AI must know?",
    hint: "Conditions, formulas, exclusions, or definitions used in your analysis and reports.",
    example:
      "- dpd > 0 → customer is delinquent\n- Write-off loans (status = 'WO') are excluded from active portfolio metrics\n- EMI = principal + interest combined (not stored separately)",
    placeholder:
      "- condition → what it means\n- formula or rule description\n- any important exclusions…",
  },
  {
    id: "dates",
    icon: "📅",
    title: "Date & Time Logic",
    required: false,
    group: "extended",
    question: "How are dates used or derived in this database?",
    hint: "Date field meanings, derived calculations, and fiscal year definition if relevant.",
    example:
      "- age: derived from date_of_birth at time of loan disbursement\n- loan tenure: loan_opening_date to loan_maturity_date\n- Fiscal year runs April to March (India)",
    placeholder:
      "- date_field: how it is used or derived\n- fiscal year definition\n- any time-based calculations…",
  },
  {
    id: "warnings",
    icon: "🚨",
    title: "Warnings & Edge Cases",
    required: false,
    group: "extended",
    question: "What should the AI be careful about?",
    hint: "NULL values, units, outliers, data quality issues, or records to exclude.",
    example:
      "- Some customers have NULL income — exclude from average income queries\n- All monetary values are in INR (not thousands or lakhs)\n- interest_rate is stored as percentage (12.5, not 0.125)",
    placeholder:
      "- edge case or data quirk to watch for\n- known NULL issues or outliers\n- units or format notes…",
  },
];

const CORE = SECTIONS.filter((s) => s.group === "core");
const EXTENDED = SECTIONS.filter((s) => s.group === "extended");

/* ─────────────────────────────────────────────────────────
   Helpers
───────────────────────────────────────────────────────── */
function compileSections(values) {
  const filled = SECTIONS.filter((s) => values[s.id]?.trim());
  if (!filled.length) return "";
  return filled
    .map((s) => `${s.icon} ${s.title.toUpperCase()}\n${"─".repeat(44)}\n${values[s.id].trim()}`)
    .join("\n\n");
}

function parseTextToSections(text) {
  if (!text?.trim()) return {};
  const result = {};
  const patterns = [
    { id: "overview", marks: ["DATABASE OVERVIEW", "DB OVERVIEW"] },
    { id: "tables", marks: ["TABLES & THEIR", "TABLES &", "TABLE"] },
    { id: "purpose", marks: ["WHY THIS DATA", "PURPOSE", "KEY METRICS / IMPORTANT", "IMPORTANT THINGS"] },
    { id: "business", marks: ["BUSINESS CONTEXT", "BUSINESS IT REPRESENT", "BUSINESS"] },
    { id: "metrics", marks: ["KEY METRICS", "IMPORTANT COLUMNS", "METRICS"] },
    { id: "rules", marks: ["BUSINESS RULES", "RULES & LOGIC", "RULES"] },
    { id: "dates", marks: ["DATE & TIME", "DATE LOGIC", "DATE"] },
    { id: "warnings", marks: ["EDGE CASES", "WARNINGS", "WARNING"] },
  ];
  const lines = text.split("\n");
  let curId = null;
  let curLines = [];
  const flush = () => {
    if (curId) {
      const content = curLines
        .filter((l) => l && !l.match(/^[─═\-─]+$/) && !l.startsWith("👉") && !l.startsWith("Example:"))
        .join("\n")
        .trim();
      if (content.length > 10) result[curId] = content;
    }
    curLines = [];
  };
  for (const line of lines) {
    const upper = line.toUpperCase();
    if (upper.includes("GLOBAL AI CONTEXT") || upper.includes("VERY IMPORTANT") || upper.includes("TIP:")) continue;
    let matched = null;
    for (const { id, marks } of patterns) {
      if (marks.some((m) => upper.includes(m))) { matched = id; break; }
    }
    if (matched) { flush(); curId = matched; }
    else if (curId) curLines.push(line);
  }
  flush();
  return result;
}

function getSectionQuality(values) {
  const reqFilled = CORE.filter((s) => s.required && values[s.id]?.trim().length > 40).length;
  const totalFilled = SECTIONS.filter((s) => values[s.id]?.trim().length > 0).length;
  if (!Object.values(values).some((v) => v?.trim())) return null;
  if (reqFilled < 1) return { label: "Too Brief", color: "text-red-500", bg: "#ef4444", bar: 10 };
  if (reqFilled < 2) return { label: "Basic", color: "text-amber-500", bg: "#f59e0b", bar: 35 };
  if (reqFilled < 3) return { label: "Good Start", color: "text-amber-400", bg: "#fbbf24", bar: 55 };
  if (totalFilled < 5) return { label: "Good", color: "text-blue-500", bg: "#3b82f6", bar: 70 };
  if (totalFilled < 7) return { label: "Great", color: "text-emerald-500", bg: "#10b981", bar: 87 };
  return { label: "Excellent", color: "text-emerald-400", bg: "#10b981", bar: 100 };
}

function getExpertQuality(charCount) {
  if (!charCount) return null;
  if (charCount < 200) return { label: "Too Brief", color: "text-red-500", bg: "#ef4444", bar: 10 };
  if (charCount < 600) return { label: "Basic", color: "text-amber-500", bg: "#f59e0b", bar: 35 };
  if (charCount < 1000) return { label: "Good", color: "text-blue-500", bg: "#3b82f6", bar: 65 };
  if (charCount < 1800) return { label: "Great", color: "text-emerald-500", bg: "#10b981", bar: 85 };
  return { label: "Excellent", color: "text-emerald-400", bg: "#10b981", bar: 100 };
}

/* ─────────────────────────────────────────────────────────
   Sub-components
───────────────────────────────────────────────────────── */
function SectionCard({ section, value, onChange, showExample, onToggleExample }) {
  const isDone = value.trim().length >= 40;
  const isPartial = value.trim().length > 0 && !isDone;

  return (
    <div
      className="rounded-xl border overflow-hidden transition-all duration-200"
      style={{
        borderColor: isDone
          ? "color-mix(in srgb, #10b981 35%, transparent)"
          : isPartial
          ? "color-mix(in srgb, var(--color-primary) 30%, transparent)"
          : "var(--color-border)",
      }}
    >
      {/* Card Header */}
      <div
        className="flex items-start justify-between px-4 py-3 gap-3"
        style={{
          background: isDone
            ? "color-mix(in srgb, #10b981 5%, transparent)"
            : isPartial
            ? "color-mix(in srgb, var(--color-primary) 4%, transparent)"
            : "color-mix(in srgb, var(--color-muted) 30%, transparent)",
        }}
      >
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <span className="text-lg leading-none mt-0.5 flex-shrink-0">{section.icon}</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-foreground">{section.title}</span>
              {section.required ? (
                <span
                  className="text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider"
                  style={{
                    background: "color-mix(in srgb, var(--color-primary) 12%, transparent)",
                    color: "var(--color-primary)",
                  }}
                >
                  Required
                </span>
              ) : (
                <span className="text-[9px] px-1.5 py-0.5 rounded font-medium uppercase tracking-wide text-muted-foreground bg-muted/50">
                  Optional
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{section.question}</p>
          </div>
        </div>
        {/* Status dot */}
        <div className="flex-shrink-0 mt-0.5">
          {isDone ? (
            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-500/15">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
            </span>
          ) : isPartial ? (
            <span
              className="flex items-center justify-center w-6 h-6 rounded-full"
              style={{ background: "color-mix(in srgb, var(--color-primary) 12%, transparent)" }}
            >
              <Circle
                className="w-3 h-3"
                style={{ color: "var(--color-primary)" }}
                strokeDasharray="12 50"
              />
            </span>
          ) : (
            <span className="flex items-center justify-center w-6 h-6 rounded-full border border-border">
              <Circle className="w-3 h-3 text-border" />
            </span>
          )}
        </div>
      </div>

      {/* Hint + Example toggle */}
      <div className="px-4 pt-2.5 pb-1">
        <p className="text-xs text-muted-foreground leading-relaxed">{section.hint}</p>
        <button
          onClick={onToggleExample}
          className="mt-1.5 flex items-center gap-1.5 text-[11px] font-medium transition-colors hover:opacity-80"
          style={{ color: "var(--color-primary)" }}
        >
          {showExample ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
          {showExample ? "Hide example" : "See an example"}
        </button>

        {showExample && (
          <div
            className="mt-2 p-3 rounded-lg border text-xs text-muted-foreground font-mono whitespace-pre-wrap leading-relaxed"
            style={{
              background: "color-mix(in srgb, var(--color-muted) 40%, transparent)",
              borderColor: "color-mix(in srgb, var(--color-border) 60%, transparent)",
            }}
          >
            {section.example}
          </div>
        )}
      </div>

      {/* Textarea */}
      <textarea
        rows={value.trim() ? Math.max(3, value.split("\n").length + 1) : 3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={section.placeholder}
        className="w-full bg-transparent px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none resize-none font-sans leading-relaxed"
        style={{ minHeight: "72px" }}
      />

      {/* Footer bar */}
      <div
        className="flex items-center justify-between px-4 py-1.5 border-t"
        style={{ borderColor: "var(--color-border)", background: "color-mix(in srgb, var(--color-muted) 20%, transparent)" }}
      >
        <span className="text-[10px] text-muted-foreground">
          {value.length} chars
          {section.required && value.trim().length < 40 && value.trim().length > 0 && (
            <span className="ml-1.5 text-amber-500">— add more for best results</span>
          )}
        </span>
        {isDone && <span className="text-[10px] text-emerald-500 font-medium">✓ Complete</span>}
      </div>
    </div>
  );
}

function EmptyStateWelcome({ onLoadTemplate, onSwitchExpert }) {
  return (
    <div
      className="rounded-2xl border-2 border-dashed p-8 text-center space-y-5"
      style={{ borderColor: "color-mix(in srgb, var(--color-primary) 25%, transparent)" }}
    >
      {/* Icon */}
      <div className="flex justify-center">
        <div
          className="w-16 h-16 rounded-2xl flex items-center justify-center"
          style={{ background: "color-mix(in srgb, var(--color-primary) 10%, transparent)" }}
        >
          <Brain className="w-8 h-8" style={{ color: "var(--color-primary)" }} />
        </div>
      </div>

      {/* Text */}
      <div>
        <h3 className="text-base font-semibold text-foreground">No AI context added yet</h3>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-sm mx-auto leading-relaxed">
          Help the AI understand your database. The better this context, the more accurate your queries, insights, and dashboards will be.
        </p>
      </div>

      {/* 3 value props */}
      <div className="grid grid-cols-3 gap-3 max-w-lg mx-auto text-left">
        {[
          { icon: "🎯", title: "Better Queries", desc: "AI writes accurate SQL first time" },
          { icon: "💡", title: "Smarter Insights", desc: "Understands your business terms" },
          { icon: "⚡", title: "Less Back-and-Forth", desc: "No need to explain every time" },
        ].map((v) => (
          <div
            key={v.title}
            className="rounded-xl p-3 border text-xs"
            style={{
              background: "color-mix(in srgb, var(--color-muted) 30%, transparent)",
              borderColor: "var(--color-border)",
            }}
          >
            <span className="text-base">{v.icon}</span>
            <p className="font-semibold text-foreground mt-1.5">{v.title}</p>
            <p className="text-muted-foreground mt-0.5 leading-relaxed">{v.desc}</p>
          </div>
        ))}
      </div>

      {/* CTAs */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
        <div
          className="flex items-center gap-2 text-xs font-medium px-4 py-2 rounded-lg"
          style={{
            background: "color-mix(in srgb, var(--color-primary) 10%, transparent)",
            color: "var(--color-primary)",
          }}
        >
          <ArrowRight className="w-3.5 h-3.5" />
          Fill the sections below — guided and easy
        </div>
        <span className="text-xs text-muted-foreground">or</span>
        <button
          onClick={onLoadTemplate}
          className="flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg border border-border hover:bg-muted/40 transition-colors"
        >
          <Wand2 className="w-3.5 h-3.5 text-amber-500" />
          Load a ready-made template
        </button>
        <button
          onClick={onSwitchExpert}
          className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
        >
          Expert mode (write directly)
        </button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Main Component
───────────────────────────────────────────────────────── */
export default function GeneralInstructionTab({ sourceDbId }) {
  const [instructionId, setInstructionId] = useState(null);
  const [instructionText, setInstructionText] = useState("");
  const [initialText, setInitialText] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Mode: 'form' = structured section cards | 'expert' = raw textarea
  const [mode, setMode] = useState("form");
  const [sectionValues, setSectionValues] = useState({});
  const [initialSectionValues, setInitialSectionValues] = useState({});
  const [showExamples, setShowExamples] = useState({});
  const [extendedOpen, setExtendedOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const { registerJob, getJob, retryJob } = useIngestionJobs({
    sourceDbId,
    resourceTypes: ["general_instruction"],
  });

  /* ── Load ── */
  const loadInstruction = useCallback(async () => {
    if (!sourceDbId) return;
    setLoading(true);
    try {
      const res = await getGeneralInstructionBySource(sourceDbId);
      const data = Array.isArray(res.data) ? res.data[0] : null;
      if (data) {
        const text = data.instruction_text || "";
        setInstructionId(data.id);
        setInstructionText(text);
        setInitialText(text);
        const parsed = parseTextToSections(text);
        if (Object.keys(parsed).length >= 2) {
          setSectionValues(parsed);
          setInitialSectionValues(parsed);
          setMode("form");
        } else if (text) {
          // Existing freeform text → show in expert mode
          setMode("expert");
        }
      } else {
        setMode("form");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to load general instruction");
    } finally {
      setLoading(false);
    }
  }, [sourceDbId]);

  useEffect(() => { loadInstruction(); }, [loadInstruction]);

  /* ── Mode switching ── */
  const switchToExpert = () => {
    const compiled = compileSections(sectionValues);
    if (compiled) setInstructionText(compiled);
    setMode("expert");
  };

  const switchToForm = () => {
    const parsed = parseTextToSections(instructionText);
    if (Object.keys(parsed).length >= 1) setSectionValues(parsed);
    setMode("form");
  };

  /* ── Save ── */
  const handleSave = async () => {
    let textToSave = instructionText;
    if (mode === "form") {
      textToSave = compileSections(sectionValues);
      if (!textToSave.trim()) { toast.error("Please fill in at least one section"); return; }
    } else if (!textToSave.trim()) {
      toast.error("Instruction cannot be empty"); return;
    }
    setSaving(true);
    try {
      let response;
      if (instructionId) {
        response = await updateGeneralInstruction(instructionId, { instruction_text: textToSave });
        toast.success("Instruction updated and re-indexed");
      } else {
        response = await createGeneralInstruction({ source_db_id: sourceDbId, instruction_text: textToSave });
        setInstructionId(response.data.data.id);
        toast.success("Instruction saved and indexed");
      }
      const savedId = response?.data?.data?.id || instructionId;
      if (response?.data?.ingestion) {
        registerJob({
          summary: response.data.ingestion,
          sourceDbId,
          resourceId: savedId,
          label: "General guidance",
        });
      }
      setInstructionText(textToSave);
      setInitialText(textToSave);
      setInitialSectionValues({ ...sectionValues });
    } catch (err) {
      console.error(err);
      toast.error("Failed to save instruction");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const handleDelete = async () => {
    if (!instructionId) return;
    if (!window.confirm("Delete this instruction? This will also remove it from the AI knowledge base.")) return;
    setDeleting(true);
    try {
      const deletingId = instructionId;
      const response = await deleteGeneralInstruction(instructionId);
      if (response?.data?.ingestion) {
        registerJob({
          summary: response.data.ingestion,
          sourceDbId,
          resourceId: deletingId,
          label: "General guidance",
        });
      }
      toast.success("Instruction deleted");
      setInstructionId(null);
      setInstructionText("");
      setInitialText("");
      setSectionValues({});
      setInitialSectionValues({});
      setMode("form");
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete instruction");
    } finally {
      setDeleting(false);
    }
  };

  /* ── Template ── */
  const handleUseTemplate = () => {
    const seeds = {
      overview: "Describe your database here — what it tracks and what business problem it solves.",
      tables: "- table_name: what it stores\n- another_table: what it contains",
      purpose: "Explain what decisions, reports, or analyses this data is used for.",
      business: "Describe the company, domain, and industry context.",
    };
    setSectionValues(seeds);
    setMode("form");
    setExtendedOpen(false);
    toast.info("Template loaded — replace each section with your database details");
  };

  /* ── Dirty check ── */
  const isDirty =
    mode === "form"
      ? JSON.stringify(sectionValues) !== JSON.stringify(initialSectionValues)
      : instructionText !== initialText;

  /* ── Quality ── */
  const formHasAnyContent = SECTIONS.some((s) => sectionValues[s.id]?.trim());
  const quality =
    mode === "form"
      ? getSectionQuality(sectionValues)
      : getExpertQuality(instructionText.length);

  const filledSections = SECTIONS.filter((s) => sectionValues[s.id]?.trim()).length;
  const requiredFilled = CORE.filter((s) => s.required && sectionValues[s.id]?.trim().length >= 40).length;
  const totalRequired = CORE.filter((s) => s.required).length;

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-muted-foreground py-12 justify-center">
        <Loader2 className="w-4 h-4 animate-spin" />
        <span className="text-sm">Loading…</span>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-4 font-sans">

      <IngestionQueueStrip sourceDbId={sourceDbId} resourceTypes={["general_instruction"]} />

      {/* ── Hero Banner ── */}
      <div
        className="rounded-xl p-4 border"
        style={{
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 8%, transparent), color-mix(in srgb, var(--color-primary) 3%, transparent))",
          borderColor: "color-mix(in srgb, var(--color-primary) 20%, transparent)",
        }}
      >
        <div className="flex items-start gap-3">
          <div
            className="rounded-lg p-2 flex-shrink-0"
            style={{ background: "color-mix(in srgb, var(--color-primary) 14%, transparent)" }}
          >
            <Brain className="w-5 h-5" style={{ color: "var(--color-primary)" }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-sm font-semibold text-foreground">Global AI Context</h2>
              <span
                className="text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider"
                style={{
                  background: "color-mix(in srgb, var(--color-primary) 14%, transparent)",
                  color: "var(--color-primary)",
                }}
              >
                Very Important
              </span>
              {instructionId && (
                <span className="flex items-center gap-1 text-[10px] text-emerald-500 font-medium">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                  Active
                </span>
              )}
              {instructionId && (
                <IngestionStatusBadge
                  job={getJob("general_instruction", instructionId)}
                  onRetry={() => retryJob(getJob("general_instruction", instructionId)?.jobId)}
                />
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              This is the AI's briefing about your database.{" "}
              <span className="font-medium text-foreground">The more you add, the smarter and more accurate the AI becomes</span>{" "}
              — like onboarding a new analyst before they touch your data.
            </p>
          </div>
        </div>

        {/* Quick stat row */}
        <div className="grid grid-cols-3 gap-2 mt-3">
          {[
            { label: "What to write?", value: "Tables, KPIs, business rules, date logic" },
            { label: "Why it matters?", value: "AI uses this for every query & insight" },
            { label: "How detailed?", value: `${filledSections}/${SECTIONS.length} sections filled` },
          ].map((pill) => (
            <div
              key={pill.label}
              className="rounded-lg p-2.5 border text-xs"
              style={{
                background: "color-mix(in srgb, var(--color-background) 75%, transparent)",
                borderColor: "color-mix(in srgb, var(--color-border) 60%, transparent)",
              }}
            >
              <p className="font-semibold text-foreground mb-0.5">{pill.label}</p>
              <p className="text-muted-foreground leading-relaxed">{pill.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Mode Toggle ── */}
      <div className="flex items-center justify-between">
        <div
          className="flex items-center rounded-lg border p-0.5 gap-0.5"
          style={{ background: "color-mix(in srgb, var(--color-muted) 40%, transparent)" }}
        >
          <button
            onClick={switchToForm}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
            style={
              mode === "form"
                ? {
                    background: "var(--color-background)",
                    color: "var(--color-foreground)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  }
                : { color: "var(--color-muted-foreground)" }
            }
          >
            <Layers className="w-3.5 h-3.5" />
            Guided Form
          </button>
          <button
            onClick={switchToExpert}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all"
            style={
              mode === "expert"
                ? {
                    background: "var(--color-background)",
                    color: "var(--color-foreground)",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
                  }
                : { color: "var(--color-muted-foreground)" }
            }
          >
            <Edit3 className="w-3.5 h-3.5" />
            Expert Editor
          </button>
        </div>

        {/* Quality badge (right side) */}
        {quality && (
          <div className="flex items-center gap-2">
            <div className="w-24 h-1.5 rounded-full overflow-hidden" style={{ background: "var(--color-border)" }}>
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${quality.bar}%`, background: quality.bg }}
              />
            </div>
            <span className={`text-[11px] font-semibold ${quality.color}`}>{quality.label}</span>
          </div>
        )}
      </div>

      {/* ── FORM MODE ── */}
      {mode === "form" && (
        <>
          {/* Empty state */}
          {!formHasAnyContent && (
            <EmptyStateWelcome onLoadTemplate={handleUseTemplate} onSwitchExpert={() => setMode("expert")} />
          )}

          {/* Section progress bar (when has content) */}
          {formHasAnyContent && (
            <div
              className="flex items-center justify-between px-4 py-2.5 rounded-xl border text-xs"
              style={{
                background: "color-mix(in srgb, var(--color-muted) 25%, transparent)",
                borderColor: "var(--color-border)",
              }}
            >
              <div className="flex items-center gap-3">
                <Info className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">
                  Required sections:{" "}
                  <span className={`font-semibold ${requiredFilled === totalRequired ? "text-emerald-500" : "text-foreground"}`}>
                    {requiredFilled}/{totalRequired} complete
                  </span>
                  <span className="mx-2 text-border">·</span>
                  All sections:{" "}
                  <span className="font-semibold text-foreground">{filledSections}/{SECTIONS.length}</span>
                </span>
              </div>
              <button
                onClick={handleUseTemplate}
                className="flex items-center gap-1 text-[11px] font-medium hover:opacity-80 transition-opacity"
                style={{ color: "var(--color-primary)" }}
              >
                <Wand2 className="w-3 h-3" />
                Use template
              </button>
            </div>
          )}

          {/* Core sections */}
          <div className="space-y-3">
            {CORE.map((section) => (
              <SectionCard
                key={section.id}
                section={section}
                value={sectionValues[section.id] || ""}
                onChange={(val) => setSectionValues((prev) => ({ ...prev, [section.id]: val }))}
                showExample={!!showExamples[section.id]}
                onToggleExample={() =>
                  setShowExamples((prev) => ({ ...prev, [section.id]: !prev[section.id] }))
                }
              />
            ))}
          </div>

          {/* Extended / optional sections */}
          <div
            className="rounded-xl border overflow-hidden"
            style={{ borderColor: "var(--color-border)" }}
          >
            <button
              onClick={() => setExtendedOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors text-left"
            >
              <div className="flex items-center gap-2.5">
                {extendedOpen ? (
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                )}
                <span className="text-sm font-medium text-foreground">
                  Advanced Context{" "}
                  <span className="font-normal text-muted-foreground ml-0.5">(optional — but improves accuracy)</span>
                </span>
              </div>
              <div className="flex items-center gap-2">
                {EXTENDED.filter((s) => sectionValues[s.id]?.trim()).length > 0 && (
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">
                    {EXTENDED.filter((s) => sectionValues[s.id]?.trim()).length}/{EXTENDED.length} filled
                  </span>
                )}
                <span className="text-xs text-muted-foreground">
                  Metrics, rules, dates, warnings
                </span>
              </div>
            </button>

            {extendedOpen && (
              <div className="border-t space-y-3 p-3" style={{ borderColor: "var(--color-border)" }}>
                {EXTENDED.map((section) => (
                  <SectionCard
                    key={section.id}
                    section={section}
                    value={sectionValues[section.id] || ""}
                    onChange={(val) => setSectionValues((prev) => ({ ...prev, [section.id]: val }))}
                    showExample={!!showExamples[section.id]}
                    onToggleExample={() =>
                      setShowExamples((prev) => ({ ...prev, [section.id]: !prev[section.id] }))
                    }
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ── EXPERT MODE ── */}
      {mode === "expert" && (
        <div className="rounded-xl border overflow-hidden" style={{ borderColor: "var(--color-border)" }}>
          {/* Header */}
          <div
            className="flex items-center justify-between px-4 py-2.5 border-b"
            style={{
              background: "color-mix(in srgb, var(--color-muted) 25%, transparent)",
              borderColor: "var(--color-border)",
            }}
          >
            <div className="flex items-center gap-2">
              <Edit3 className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-foreground">Expert Editor</span>
              <span className="text-xs text-muted-foreground">— write your AI instruction directly</span>
            </div>
            <button
              onClick={() => {
                const text =
                  "📊 DATABASE OVERVIEW\n────────────────────────────────────────────\n(Describe your database here)\n\n📁 TABLES & THEIR PURPOSE\n────────────────────────────────────────────\n- table_name: what it stores\n\n🎯 WHY THIS DATA EXISTS\n────────────────────────────────────────────\n(What decisions does this data drive?)\n\n🏢 BUSINESS CONTEXT\n────────────────────────────────────────────\n(What business or industry does this represent?)";
                setInstructionText(text);
              }}
              className="flex items-center gap-1.5 text-[11px] font-medium hover:opacity-80 transition-opacity"
              style={{ color: "var(--color-primary)" }}
            >
              <Sparkles className="w-3 h-3" />
              Insert structure
            </button>
          </div>

          <textarea
            rows={18}
            value={instructionText}
            onChange={(e) => setInstructionText(e.target.value)}
            placeholder={`Describe your database in plain language — cover these areas:\n\n📊 DATABASE OVERVIEW\nWhat is this database about? What business problem does it solve?\n\n📁 TABLES & PURPOSE\nList each table and what it stores.\n\n🎯 WHY THIS DATA EXISTS\nWhat reports, decisions, or queries does this data drive?\n\n🏢 BUSINESS CONTEXT (optional)\nWhat company/industry/domain is this for?\n\n📈 KEY METRICS & COLUMNS (optional)\nDefine important columns and their business meaning.\n\n⚠️ BUSINESS RULES (optional)\nAny logic, conditions, or exclusions the AI should know.`}
            className="no-scrollbar w-full bg-background text-foreground px-4 py-3 text-sm leading-relaxed font-mono focus:outline-none resize-none border-0"
            style={{ minHeight: "360px" }}
          />

          {/* Footer */}
          <div
            className="flex items-center justify-between px-4 py-2 border-t"
            style={{
              borderColor: "var(--color-border)",
              background: "color-mix(in srgb, var(--color-muted) 15%, transparent)",
            }}
          >
            <span className="text-[11px] text-muted-foreground">
              {instructionText.length.toLocaleString()} characters
              {instructionText.length > 0 && instructionText.length < 600 && (
                <span className="ml-2 text-amber-500">— aim for 600+ for best AI accuracy</span>
              )}
            </span>
            {isDirty && (
              <span className="text-[11px] text-amber-500 font-medium">Unsaved changes</span>
            )}
          </div>
        </div>
      )}

      {/* ── Footer ── */}
      <div className="flex items-center justify-between pb-2">
        <p className="text-xs text-muted-foreground">
          {instructionId ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
              Active — AI is using this context for this database
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
              Not saved yet — AI has no context for this database
            </span>
          )}
        </p>

        <div className="flex gap-2">
          {instructionId && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-border text-destructive hover:bg-destructive/10 disabled:opacity-50 transition-colors"
            >
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete
            </button>
          )}

          <button
            onClick={handleSave}
            disabled={!isDirty || saving}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {instructionId ? "Update & Re-index" : "Save & Index"}
          </button>
        </div>
      </div>
    </div>
  );
}
